import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Layers, Pencil, Trash2, Eye, EyeOff, Loader2, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import {
  adminListGearLibrary, adminGenerateGearLibrary, adminUpdateGearLibrary,
  adminPublishGearLibrary, adminUnpublishGearLibrary, adminDeleteGearLibrary,
} from '@/lib/api';
import { ADMIN_GEAR_LIBRARY } from '@/constants/testIds';

const TYPE_OPTIONS = [
  { value: 'camera', label: 'Camera Body' },
  { value: 'lens', label: 'Lens' },
  { value: 'lighting', label: 'Lighting & Grip' },
  { value: 'article', label: 'Technique Article' },
];

const GENERATE_EMPTY = { type: 'camera', title: '', brand: '', hints: '' };

// Common gear a studio's library is likely to need — lets an admin seed the
// reference library in bulk instead of drafting each entry one at a time.
const BULK_PRESETS = [
  { key: 'sony-a7iv', type: 'camera', title: 'A7 IV', brand: 'Sony' },
  { key: 'sony-a7riv', type: 'camera', title: 'A7R V', brand: 'Sony' },
  { key: 'canon-r5', type: 'camera', title: 'EOS R5', brand: 'Canon' },
  { key: 'canon-r6ii', type: 'camera', title: 'EOS R6 Mark II', brand: 'Canon' },
  { key: 'nikon-z6iii', type: 'camera', title: 'Z6 III', brand: 'Nikon' },
  { key: 'fuji-xt5', type: 'camera', title: 'X-T5', brand: 'Fujifilm' },
  { key: 'sony-2470gm2', type: 'lens', title: '24-70mm f/2.8 GM II', brand: 'Sony' },
  { key: 'sony-85gm', type: 'lens', title: '85mm f/1.4 GM', brand: 'Sony' },
  { key: 'canon-70200', type: 'lens', title: 'RF 70-200mm f/2.8', brand: 'Canon' },
  { key: 'sigma-35art', type: 'lens', title: '35mm f/1.4 DG DN Art', brand: 'Sigma' },
  { key: 'godox-ad200', type: 'lighting', title: 'AD200 Pro', brand: 'Godox' },
  { key: 'godox-ad600', type: 'lighting', title: 'AD600 Pro', brand: 'Godox' },
  { key: 'profoto-b10', type: 'lighting', title: 'B10 Plus', brand: 'Profoto' },
  { key: 'neewer-660', type: 'lighting', title: '660 RGB LED Panel', brand: 'Neewer' },
];

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';

export const AdminGearLibraryTab = () => {
  const [entries, setEntries] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState(GENERATE_EMPTY);
  const [generating, setGenerating] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkChecked, setBulkChecked] = useState({});
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, current: '' });

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = () => adminListGearLibrary().then(setEntries).catch(() => setEntries([]));

  useEffect(() => {
    refresh();
  }, []);

  const existingKeys = useMemo(() => {
    if (!entries) return new Set();
    return new Set(entries.map((e) => `${(e.brand || '').trim().toLowerCase()}|${e.title.trim().toLowerCase()}`));
  }, [entries]);

  const availablePresets = useMemo(
    () => BULK_PRESETS.filter((p) => !existingKeys.has(`${p.brand.toLowerCase()}|${p.title.toLowerCase()}`)),
    [existingKeys],
  );

  // ---- Single AI generate ----
  const openGenerate = () => {
    setGenForm(GENERATE_EMPTY);
    setGenOpen(true);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!genForm.title.trim()) return;
    setGenerating(true);
    try {
      await adminGenerateGearLibrary(genForm);
      toast.success('Draft generated. Review and publish it below.');
      setGenOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI drafting failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ---- Bulk AI generate ----
  const openBulk = () => {
    setBulkChecked({});
    setBulkOpen(true);
  };

  const toggleBulk = (key) => setBulkChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleBulkGenerate = async () => {
    const items = availablePresets.filter((p) => bulkChecked[p.key]);
    if (items.length === 0) return;
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: items.length, current: items[0].title });
    let succeeded = 0;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      setBulkProgress({ done: i, total: items.length, current: `${item.brand} ${item.title}` });
      try {
        await adminGenerateGearLibrary({ type: item.type, title: item.title, brand: item.brand, hints: '' });
        succeeded += 1;
      } catch {
        // Skip failures (rate limit/AI hiccup) and keep going with the rest of the batch.
      }
    }
    setBulkProgress({ done: items.length, total: items.length, current: '' });
    setBulkRunning(false);
    setBulkOpen(false);
    refresh();
    toast.success(`Generated ${succeeded} of ${items.length} draft${items.length === 1 ? '' : 's'}. Review and publish below.`);
  };

  // ---- Edit ----
  const openEdit = (entry) => {
    setEditing(entry);
    setEditForm({
      title: entry.title,
      brand: entry.brand || '',
      summary: entry.summary || '',
      content: entry.content || '',
      good_for: (entry.good_for || []).join(', '),
      aliases: (entry.aliases || []).join(', '),
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminUpdateGearLibrary(editing.id, {
        title: editForm.title,
        brand: editForm.brand,
        summary: editForm.summary,
        content: editForm.content,
        good_for: editForm.good_for.split(',').map((t) => t.trim()).filter(Boolean),
        aliases: editForm.aliases.split(',').map((t) => t.trim()).filter(Boolean),
      });
      toast.success('Entry updated.');
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  // ---- Publish / unpublish / delete ----
  const togglePublish = async (entry) => {
    try {
      if (entry.status === 'published') {
        await adminUnpublishGearLibrary(entry.id);
        toast.success('Entry unpublished.');
      } else {
        await adminPublishGearLibrary(entry.id);
        toast.success('Entry published to the public Gear Guide.');
      }
      refresh();
    } catch {
      toast.error('Could not change publish status.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminDeleteGearLibrary(deleteTarget.id);
      toast.success('Entry deleted.');
      setDeleteTarget(null);
      refresh();
    } catch {
      toast.error('Could not delete entry.');
    }
  };

  return (
    <div data-testid={ADMIN_GEAR_LIBRARY.root}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[hsl(var(--lxra-muted))] max-w-md">
          AI-drafted reference entries for cameras, lenses, lighting, and technique — reviewed and published by you before they go live on <span className="lxra-mono">/gear</span>.
        </p>
        <div className="flex items-center gap-2">
          <Button
            onClick={openBulk}
            variant="outline"
            data-testid={ADMIN_GEAR_LIBRARY.bulkButton}
            className="h-10 px-4 rounded-lg border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]"
          >
            <Layers className="h-4 w-4 mr-1.5" />
            Bulk Generate
          </Button>
          <Button
            onClick={openGenerate}
            data-testid={ADMIN_GEAR_LIBRARY.generateButton}
            className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            Generate with AI
          </Button>
        </div>
      </div>

      {!entries && <Skeleton className="h-64 rounded-2xl bg-[#141416]" />}

      {entries && entries.length === 0 && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
          <p className="text-sm text-[hsl(var(--lxra-muted))]">No gear entries yet. Generate your first draft with AI, or bulk-seed common gear above.</p>
        </div>
      )}

      {entries && entries.length > 0 && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden overflow-x-auto" data-testid={ADMIN_GEAR_LIBRARY.table}>
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Title</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Type</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Status</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} data-testid={ADMIN_GEAR_LIBRARY.row(entry.id)} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                  <TableCell className="text-[hsl(var(--lxra-fg))]">
                    <div className="flex items-center gap-2">
                      {entry.brand ? `${entry.brand} ${entry.title}` : entry.title}
                      {entry.ai_generated && (
                        <span title="AI-drafted"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /></span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] capitalize">{entry.type}</TableCell>
                  <TableCell>
                    <Badge
                      data-testid={ADMIN_GEAR_LIBRARY.statusBadge(entry.id)}
                      className={entry.status === 'published'
                        ? 'bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)] font-normal'
                        : 'bg-transparent text-[hsl(var(--lxra-muted))] border border-[hsl(var(--lxra-border))] font-normal'}
                    >
                      {entry.status === 'published' ? <BadgeCheck className="h-3 w-3 mr-1" /> : null}
                      {entry.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => togglePublish(entry)}
                      data-testid={entry.status === 'published' ? ADMIN_GEAR_LIBRARY.unpublishButton(entry.id) : ADMIN_GEAR_LIBRARY.publishButton(entry.id)}
                      className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[#1a1a1c]"
                      title={entry.status === 'published' ? 'Unpublish' : 'Publish'}
                    >
                      {entry.status === 'published' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(entry)} data-testid={ADMIN_GEAR_LIBRARY.editButton(entry.id)} className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[#1a1a1c]">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(entry)} data-testid={ADMIN_GEAR_LIBRARY.deleteButton(entry.id)} className="text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Single AI generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid={ADMIN_GEAR_LIBRARY.generateDialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">Generate Gear Entry with AI</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Type</Label>
              <Select value={genForm.type} onValueChange={(v) => setGenForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid={ADMIN_GEAR_LIBRARY.generateTypeSelect} className={fieldClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                  {TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Title</Label>
              <Input required value={genForm.title} onChange={(e) => setGenForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. A7 IV" data-testid={ADMIN_GEAR_LIBRARY.generateTitleInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Brand (optional)</Label>
              <Input value={genForm.brand} onChange={(e) => setGenForm((f) => ({ ...f, brand: e.target.value }))} placeholder="e.g. Sony" data-testid={ADMIN_GEAR_LIBRARY.generateBrandInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Hints for the AI (optional)</Label>
              <Textarea value={genForm.hints} onChange={(e) => setGenForm((f) => ({ ...f, hints: e.target.value }))} placeholder="Anything specific you want covered..." data-testid={ADMIN_GEAR_LIBRARY.generateHintsInput} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] resize-none" rows={3} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={generating} data-testid={ADMIN_GEAR_LIBRARY.generateSubmit} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate Draft'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk AI generate dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => !bulkRunning && setBulkOpen(v)}>
        <DialogContent data-testid={ADMIN_GEAR_LIBRARY.bulkDialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">Bulk Generate Common Gear</DialogTitle>
          </DialogHeader>
          {availablePresets.length === 0 ? (
            <p className="text-sm text-[hsl(var(--lxra-muted))]">Every preset item already exists in your library. Use "Generate with AI" for anything else.</p>
          ) : (
            <>
              <p className="text-sm text-[hsl(var(--lxra-muted))]">Select the gear you'd like AI to draft. Each selected item runs as its own draft you can review before publishing.</p>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {TYPE_OPTIONS.map((opt) => {
                  const group = availablePresets.filter((p) => p.type === opt.value);
                  if (group.length === 0) return null;
                  return (
                    <div key={opt.value}>
                      <p className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))] mb-2">{opt.label}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {group.map((p) => (
                          <label key={p.key} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] px-3 py-2 text-sm text-[hsl(var(--lxra-fg))] cursor-pointer hover:border-[hsl(var(--lxra-gold))]/40">
                            <Checkbox
                              checked={!!bulkChecked[p.key]}
                              onCheckedChange={() => toggleBulk(p.key)}
                              data-testid={ADMIN_GEAR_LIBRARY.bulkItemCheckbox(p.key)}
                            />
                            {p.brand} {p.title}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {bulkRunning && (
                <p data-testid={ADMIN_GEAR_LIBRARY.bulkProgress} className="text-xs text-[hsl(var(--lxra-muted))]">
                  Generating {bulkProgress.done + 1} of {bulkProgress.total}: {bulkProgress.current}...
                </p>
              )}
              <DialogFooter>
                <Button
                  onClick={handleBulkGenerate}
                  disabled={bulkRunning || Object.values(bulkChecked).every((v) => !v)}
                  data-testid={ADMIN_GEAR_LIBRARY.bulkSubmit}
                  className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
                >
                  {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate Selected'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid={ADMIN_GEAR_LIBRARY.editDialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">Edit Gear Entry</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className={labelClass}>Title</Label>
                <Input required value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} data-testid={ADMIN_GEAR_LIBRARY.editTitleInput} className={fieldClass} />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Brand</Label>
                <Input value={editForm.brand} onChange={(e) => setEditForm((f) => ({ ...f, brand: e.target.value }))} data-testid={ADMIN_GEAR_LIBRARY.editBrandInput} className={fieldClass} />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Summary</Label>
                <Textarea value={editForm.summary} onChange={(e) => setEditForm((f) => ({ ...f, summary: e.target.value }))} data-testid={ADMIN_GEAR_LIBRARY.editSummaryInput} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] resize-none" rows={2} />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Content (paragraphs separated by blank lines)</Label>
                <Textarea value={editForm.content} onChange={(e) => setEditForm((f) => ({ ...f, content: e.target.value }))} data-testid={ADMIN_GEAR_LIBRARY.editContentInput} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]" rows={6} />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Good For (comma separated)</Label>
                <Input value={editForm.good_for} onChange={(e) => setEditForm((f) => ({ ...f, good_for: e.target.value }))} data-testid={ADMIN_GEAR_LIBRARY.editGoodForInput} className={fieldClass} />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Aliases / EXIF names (comma separated)</Label>
                <Input value={editForm.aliases} onChange={(e) => setEditForm((f) => ({ ...f, aliases: e.target.value }))} placeholder="e.g. ILCE-7M4" data-testid={ADMIN_GEAR_LIBRARY.editAliasesInput} className={fieldClass} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid={ADMIN_GEAR_LIBRARY.editSaveButton} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[hsl(var(--lxra-fg))]">Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(var(--lxra-muted))]">
              {deleteTarget ? `"${deleteTarget.brand ? deleteTarget.brand + ' ' : ''}${deleteTarget.title}"` : ''} will be removed permanently, including from the public Gear Guide if published.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-[#141416]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500/90 text-white hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
